import { useContext, useEffect, useState } from 'react';
import './DeviceCashOperations.scss';
import { LayoutContext } from '../../context/LayoutContext';
import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { useParams } from 'react-router-dom';
import { CashLevelService } from '../../service/CashLevelService';
import { MessageContext } from '../../context/MessageContext';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../component/loadingoverlay/LoadingSpinner';
import { ReactComponent as CoinsIcon } from '../../assets/images/coins-image.svg';
import { ReactComponent as BanknotesIcon } from '../../assets/images/banknotes-image.svg';
import { FormattedUtils } from '../../utils/FormattedUtils';

const DeviceCashOperations = () => {

    const { setPageTitle, setShowPageTitle, setShowBackButton, setBackButtonLabel, setBackButtonNavigateTo, setActionButtons } = useContext(LayoutContext);
    const { showErrorMessage } = useContext(MessageContext);
    const { t } = useTranslation();
    const navigationContext = useNavigationContext();
    
    const { deviceId, currency } = useParams();
    const [device, setDevice] = useState(null);
    const [coins, setCoins] = useState(null);
    const [banknotes, setBanknotes] = useState(null);
    const [isLoading, setIsLoading] = useState(false);


    useEffect(() => {
        setShowPageTitle(false);
        setShowBackButton(true);
        setBackButtonLabel(() => <LoadingSpinner size="1rem" />);
        const backLink = `/cash-operations?${navigationContext.getContextParam()}`;
        setBackButtonNavigateTo(backLink);
    }, []);

    useEffect(() => {
        if (deviceId) {
            fetchData();
        }
    }, [deviceId]);


    const fetchData = async () => {
        try {
            setIsLoading(true);
            const data = await CashLevelService.getDetails(deviceId, currency);

            setDevice(data?.device);

            if (data?.cashLevels) {
                const coinLevels = data.cashLevels.filter(level => level.denominationType === 'COIN');
                const banknoteLevels = data.cashLevels.filter(level => level.denominationType === 'BANKNOTE');
                const coinsBalance = coinLevels.reduce((total, level) => {
                    return total + (level.count * level.value);
                }, 0);
                const banknotesBalance = banknoteLevels.reduce((total, level) => {
                    return total + (level.count * level.value);
                }, 0);
                setCoins({
                    balance: coinsBalance,
                    balanceFormatted: FormattedUtils.formatPrice(coinsBalance, currency),
                    levels: coinLevels
                });
                setBanknotes({
                    balance: banknotesBalance,
                    balanceFormatted: FormattedUtils.formatPrice(banknotesBalance, currency),
                    levels: banknoteLevels
                });
            }
        } catch (error) {
            showErrorMessage(t('cashoperations.details.error.fetch'));
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (device) {
            setBackButtonLabel(device?.name);
        }
    }, [device]);

    return (
        <div className='device-cash-operations-container'>
            <div className='balance-cards'>
                <div class="flex-1 data-card">
                    <CashLevelCard icon={CoinsIcon} label={t('cashoperations.details.banknotes-balance')}
                        balanceValue={coins?.balanceFormatted}
                        lastReplenishmentValue={'TODO'}
                        levels={coins?.levels}
                        isLoading={isLoading}
                    />
                </div>
                <div class="flex-1 data-card">
                    <CashLevelCard icon={BanknotesIcon} label={t('cashoperations.details.banknotes-balance')}
                        balanceValue={banknotes?.balanceFormatted}
                        lastReplenishmentValue={'TODO'}
                        levels={banknotes?.levels}
                        isLoading={isLoading}
                    />
                </div>
            </div>
            
        </div>
    )
}

const CashLevelCard = ({icon: IconComponent, label, balanceValue, lastReplenishmentValue, levels, isLoading}) => {
    const { t } = useTranslation();
    return (
        <div className='card-container'>
            <div className='card-main-info'>
                {IconComponent && (
                    <div className='icon'>
                        <IconComponent />
                    </div>
                )}
                <div className='label-value'>
                    <span className='label'>{label}</span>
                    {!isLoading && <span className='value mr-2'>{balanceValue}</span>}
                </div>
                <div className='ml-auto mt-auto last-replenishment'>
                    <span>{t('cashoperations.details.last-replenishment')}</span>
                    <span className='ml-3'>{lastReplenishmentValue}</span>
                </div>
                
            </div>
            <div className='separator' />

            <div className='card-levels'>
                <div className='header'> 
                    <span className='label'>{t('cashoperations.details.levels-header')}</span>
                </div>
                {!isLoading && levels && levels.length > 0 && (
                    <div className='denominations-table'>
                        <div className='denominations-row denominations-values'>
                            {levels.map((level, index) => (
                                <div key={`value-${index}`} className='denomination-cell'>
                                    {FormattedUtils.formatPrice(level.value, level.currency)}
                                </div>
                            ))}
                        </div>
                        <div className='denominations-row denominations-counts'>
                            {levels.map((level, index) => (
                                <div key={`count-${index}`} className='denomination-cell'>
                                    {level.count}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {isLoading && (
                    <div className='denominations-loading'>
                        <LoadingSpinner size="1rem" />
                    </div>
                )}
            </div>
        </div>
    )
}

export default DeviceCashOperations;